<template>
  <v-container fluid>
    <v-card max-width="800px" class="mx-auto elevation-12 br-lg" dark>
      <v-toolbar color="primary" flat>
        <v-icon left>mdi-file-download-outline</v-icon>
        <v-toolbar-title class="font-weight-bold">Exports & Kurslisten</v-toolbar-title>
      </v-toolbar>

      <v-card-text class="pa-6">
        <v-row>
          <v-col cols="12">
            <p class="subtitle-1 mb-2">1. Studio auswählen</p>
            <v-select
              v-model="studioSelection"
              :items="studios"
              item-text="name"
              item-value="code"
              label="Verfügbare Standorte"
              prepend-inner-icon="mdi-map-marker"
              outlined
              filled
              hide-details
              color="primary"></v-select>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <v-row align="start">
          <v-col cols="12" md="6">
            <p class="subtitle-1 mb-2 font-weight-bold">Wochen-Auswertungen (Excel/PDF)</p>
            <v-radio-group v-model="dateMode" row hide-details class="mt-0 mb-4">
              <v-radio label="Aktuelle Woche" value="current" color="primary"></v-radio>
              <v-radio label="Eigener Zeitraum (PDF)" value="custom" color="primary"></v-radio>
            </v-radio-group>

            <v-row v-if="dateMode === 'custom'" dense>
              <v-col cols="12" sm="6">
                <v-menu v-model="startMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="startDate"
                      label="Startdatum"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      hide-details></v-text-field>
                  </template>
                  <v-date-picker v-model="startDate" @input="startMenu = false" first-day-of-week="1" color="primary"></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" sm="6">
                <v-menu v-model="endMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="endDate"
                      label="Enddatum"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      hide-details></v-text-field>
                  </template>
                  <v-date-picker v-model="endDate" @input="endMenu = false" :min="startDate" first-day-of-week="1" color="primary"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-checkbox
              v-if="dateMode === 'custom'"
              v-model="showDates"
              label="Datum bei Wochentagen anzeigen"
              color="primary"
              hide-details
              class="mt-3"></v-checkbox>

            <v-row class="mt-4">
              <v-col cols="12" sm="6">
                <v-hover v-slot="{ hover }">
                  <v-btn
                    block
                    :elevation="hover ? 8 : 2"
                    :outlined="!hover"
                    color="success"
                    height="80"
                    class="flex-column transition-swing"
                    @click="downloadExcel">
                    <v-icon large class="mb-1">mdi-file-excel</v-icon>
                    <span>Kursliste</span>
                  </v-btn>
                </v-hover>
              </v-col>
              <v-col cols="12" sm="6">
                <v-hover v-slot="{ hover }">
                  <v-btn
                    block
                    :elevation="hover ? 8 : 2"
                    :outlined="!hover"
                    color="error"
                    height="80"
                    class="flex-column transition-swing"
                    @click="downloadPDF">
                    <v-icon large class="mb-1">mdi-file-pdf-box</v-icon>
                    <span>Wochenplan</span>
                  </v-btn>
                </v-hover>
              </v-col>
            </v-row>
            <v-btn v-if="canEditExports" block outlined class="export-edit-button mt-3" @click="openEditor('pdf')">
              <v-icon left>mdi-pencil-outline</v-icon>PDF bearbeiten & herunterladen
            </v-btn>
          </v-col>

          <v-divider vertical class="mx-4 hidden-sm-and-down"></v-divider>
          <v-divider class="my-6 hidden-md-and-up"></v-divider>

          <v-col cols="12" md="5">
            <p class="subtitle-1 mb-2 font-weight-bold">Tagesbilder für Screens (JPG/ZIP)</p>
            <p class="text-caption grey--text text--lighten-1 mb-4">Wähle einen Tag (als Start für die Woche oder Einzelbild).</p>

            <v-menu v-model="dailyMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dailyDate"
                  label="Datum wählen"
                  prepend-inner-icon="mdi-calendar-today"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                  hide-details
                  class="mb-4"></v-text-field>
              </template>
              <v-date-picker v-model="dailyDate" @input="dailyMenu = false" first-day-of-week="1" color="primary"></v-date-picker>
            </v-menu>

            <v-hover v-slot="{ hover }">
              <v-btn
                block
                :elevation="hover ? 8 : 2"
                :outlined="!hover"
                color="info"
                height="80"
                class="flex-column transition-swing mb-3"
                @click="downloadDailyImage">
                <v-icon large class="mb-1">mdi-image-outline</v-icon>
                <span>Tagesbild (JPG)</span>
              </v-btn>
            </v-hover>
            <v-btn v-if="canEditExports" block outlined class="export-edit-button mb-4" @click="openEditor('day')">
              <v-icon left>mdi-pencil-outline</v-icon>Tagesbild bearbeiten & herunterladen
            </v-btn>

            <v-hover v-slot="{ hover }">
              <v-btn
                block
                :elevation="hover ? 8 : 2"
                :outlined="!hover"
                height="80"
                class="flex-column transition-swing"
                @click="downloadWeeklyImagesZip">
                <v-icon large class="mb-1">mdi-folder-zip-outline</v-icon>
                <span>Ganze Woche (ZIP)</span>
              </v-btn>
            </v-hover>
            <v-btn v-if="canEditExports" block outlined class="export-edit-button mt-3" @click="openEditor('week')">
              <v-icon left>mdi-pencil-outline</v-icon>Wochen-ZIP bearbeiten & herunterladen
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="grey darken-4 pa-4 justify-center">
        <small class="grey--text text--lighten-1"> Wähle ein Studio aus, um die entsprechenden Daten zu exportieren. </small>
      </v-card-actions>
    </v-card>
    <group-fitness-export-editor v-if="canEditExports" v-model="editorOpen" :studio="editorStudio" :options="editorOptions"></group-fitness-export-editor>
  </v-container>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"
import GroupFitnessExportEditor from "./groupFitnessExportEditor.vue"

export default {
  name: "DownloadManager",
  components: { GroupFitnessExportEditor },
  data() {
    return {
      studios: [],
      studioSelection: null,
      dateMode: "current",
      showDates: true,
      startDate: null,
      endDate: null,
      startMenu: false,
      endMenu: false,
      dailyDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      dailyMenu: false,
      editorOpen: false,
      editorStudio: "",
      editorOptions: {},
    }
  },
  computed: {
    canEditExports() { return userService.hasRight("gf_c_plan") },
  },
  async created() {
    await this.fetchStudios()
    this.setDefaultStudio()
    this.$emit("finishedloading")
  },
  methods: {
    openEditor(format) {
      if (!this.canEditExports || !this.checkSelection()) return
      const options = { format }
      if (format === "pdf" && this.dateMode === "custom") {
        if (!this.startDate || !this.endDate || this.endDate < this.startDate) {
          this.$toast.error("Bitte wähle einen gültigen Start- und Endtermin aus")
          return
        }
        options.start = this.startDate
        options.end = this.endDate
        options.showDates = this.showDates
      } else if (format !== "pdf") {
        if (!this.dailyDate) {
          this.$toast.error("Bitte wähle ein Datum aus")
          return
        }
        options.date = this.dailyDate
      }
      this.editorStudio = String(this.studioSelection)
      this.editorOptions = options
      this.editorOpen = true
    },

    setDefaultStudio() {
      let code = this.$route.params.studiocode
      if (!code) {
        const user = JSON.parse(localStorage.getItem("user"))
        if (user && user.user) {
          code = user.user.studio_id
        }
      }
      if (code) {
        this.studioSelection = code
      }
    },

    async fetchStudios() {
      const skipStudioCodes = ["alibey", "ot", "test"]
      try {
        let response = await api.fetchStudios()
        this.studios = response.filter((s) => !skipStudioCodes.some((e) => s.code.includes(e)))
      } catch (error) {
        console.error("Fehler beim Laden der Studios:", error)
        this.$toast.error("Studios konnten nicht geladen werden")
      }
    },

    async downloadExcel() {
      if (!this.checkSelection()) return
      try {
        this.$toast.success("Excel wird generiert...")
        await api.downloadWeeklyExcel(this.studioSelection)
      } catch (error) {
        this.$toast.error("Fehler beim Excel-Download")
      }
    },

    async downloadPDF() {
      if (!this.checkSelection()) return
      let start = null
      let end = null
      if (this.dateMode === "custom") {
        if (!this.startDate || !this.endDate) {
          this.$toast.error("Bitte wähle ein Start- und Enddatum aus")
          return
        }
        start = this.startDate
        end = this.endDate
      }
      try {
        this.$toast.success("PDF wird erstellt...")
        await api.fetchGFWeeklyPDF(this.studioSelection, start, end, this.showDates)
      } catch (error) {
        console.error("Failed to download PDF:", error)
        this.$toast.error("Fehler beim PDF-Download")
      }
    },

    async downloadDailyImage() {
      if (!this.checkSelection()) return
      if (!this.dailyDate) {
        this.$toast.error("Bitte wähle ein Datum aus")
        return
      }
      try {
        this.$toast.success("Tagesbild wird generiert...")
        await api.fetchGFDailyScreen(this.studioSelection, this.dailyDate)
      } catch (error) {
        console.error("Failed to download image:", error)
        this.$toast.error("Fehler beim Bild-Download")
      }
    },

    async downloadWeeklyImagesZip() {
      if (!this.checkSelection()) return
      if (!this.dailyDate) {
        this.$toast.error("Bitte wähle ein Startdatum aus")
        return
      }
      try {
        this.$toast.info("ZIP wird erstellt, bitte warten...")
        await api.fetchGFWeeklyScreensZip(this.studioSelection, this.dailyDate)
      } catch (error) {
        console.error("Failed to download ZIP:", error)
        this.$toast.error("Fehler beim ZIP-Download")
      }
    },

    checkSelection() {
      if (!this.studioSelection) {
        this.$toast.error("Bitte wähle zuerst ein Studio aus")
        return false
      }
      return true
    },
  },
}
</script>

<style lang="scss" scoped>
.br-lg {
  border-radius: 12px !important;
}
.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.export-edit-button {
  height: auto !important;
  min-height: 3rem;
  padding: 0.75rem !important;
  font-size: 0.875rem;
  text-transform: none;
  letter-spacing: normal;
}
.export-edit-button ::v-deep .v-btn__content {
  white-space: normal;
  line-height: 1.5;
}
</style>
